import { useEffect, useRef, useState } from "react";
import { Input, InputProps } from "reactstrap";

/**
 * Text input with length validation.
 */

interface TextInputProps extends InputProps {
  trim?: boolean;
}

const TextInput = (props: TextInputProps) => {
  const { minLength, maxLength, onChange, onInvalid, trim, ...inputProps } = props;

  const [textValue, setTextValue] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);

  const messageRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const value = props.value?.toString() ?? '';
    setTextValue(trim ? value.trim() : value);
  }, [props.value, trim]);

  const checkLength = (value: string) => {
    const tooLong = !!maxLength && value.length > maxLength;
    const tooShort = !!minLength && value.length < minLength;

    let msg = '';
    if (props.required && !value) {
      msg = `${props.ariaLabel ?? 'This field'} is required.`;
    } else if (tooLong) {
      msg = `This value is too long. It should have ${maxLength} characters or fewer.`;
    } else if (tooShort) {
      msg = `Please enter at least ${minLength} characters.`;
    }

    inputRef.current?.setCustomValidity(msg);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (messageRef.current) {
      messageRef.current.style.display = 'block';
    }

    const { value } = event.target;

    setTextValue(trim ? value.trim() : value);
    checkLength(trim ? value.trim() : value);

    onChange?.(event);
  };

  const handleInvalid = (event: React.InvalidEvent<HTMLInputElement>) => {
    if (messageRef.current) {
      messageRef.current.style.setProperty('display', 'none', 'important');
    }
    onInvalid?.(event);
  };

  return (<>
    <Input {...inputProps} type="text"
      onChange={handleChange}
      onInvalid={handleInvalid}
      minLength={minLength}
      innerRef={inputRef} />
    {!!maxLength && textValue.length > maxLength && <>
      <span className="invalid-feedback d-block text-end">
        {textValue.length ?? 0}/{maxLength} Characters
      </span>
      <span className="invalid-feedback d-block" ref={messageRef}>
        {inputRef.current?.validationMessage}
      </span>
    </>}
  </>);
};

export default TextInput;
